/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { BlockSnapshot } from "@vienna/common/src";

import { BlockFactory } from "src/classes/block/BlockFactory";
import { Plate } from "src/classes/plate/Plate";
import { PositionUtils } from "src/classes/utilities/PositionUtils";
import { ONE_GRID_UNIT, TOAST_DEFAULT_ARGS } from "src/lib/constants";
//@todo better permission management

export class ClipboardManager {
  private readonly plate: Plate;

  private copiedBlocks: BlockSnapshot[] = [];

  constructor(plate: Plate) {
    this.plate = plate;
    document.addEventListener("copy", (e) => this.copy(e));
    document.addEventListener("cut", (e) => this.cut(e));
    document.addEventListener("paste", (e) => this.paste(e));
  }

  /* -------------------------------------------------- */
  /*                  Cut, Copy, Paste                  */
  /* -------------------------------------------------- */
  public cut(e: ClipboardEvent): void {
    if (!this.plate.snapshot.editable || this.plate.editingText) return;
    if (this.plate.snapshot.selection.length) {
      this.copy(e);
      this.plate.deleteBlocks(this.plate.snapshot.selection);
    }
  }

  public copy(e: ClipboardEvent): void {
    if (!this.plate.snapshot.editable || this.plate.editingText) return;
    if (this.plate.snapshot.selection.length) {
      e.stopPropagation();
      e.preventDefault();
      this.copiedBlocks = this.plate.selectedBlocks.map((block) => block.snapshot);
    }
  }

  public paste(e: ClipboardEvent): void {
    if (!this.plate.snapshot.editable || this.plate.editingText) return;
    if (this.copiedBlocks.length) {
      e.preventDefault();
      e.stopPropagation();
      const plateDimensions = { width: this.plate.snapshot.width, height: this.plate.snapshot.height };
      const maxMoves = PositionUtils.getMaxMoveForBlocks(this.copiedBlocks, plateDimensions);
      const canGoDown = maxMoves.down >= ONE_GRID_UNIT;
      const canGoRight = maxMoves.right >= ONE_GRID_UNIT;
      const newSnapshots = this.copiedBlocks.map((snapshot) => {
        return {
          ...snapshot,
          id: uuidv4(),
          top: canGoDown ? snapshot.top + ONE_GRID_UNIT : snapshot.top,
          left: canGoRight ? snapshot.left + ONE_GRID_UNIT : snapshot.left,
        };
      });
      const newBlocks = newSnapshots.map((snapshot) => BlockFactory.create(snapshot, this.plate.socket));
      this.plate.createBlocks(newBlocks);
      this.plate.deselectAll();
      this.copiedBlocks = [];
    } else {
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;
      const pastedItems = Object.values(clipboardItems);
      pastedItems.forEach((item, index) => {
        if (item.kind === "file") {
          const file = item.getAsFile() as File;
          if (file.size > 500000000) {
            toast.error("Max file size is 500MB", TOAST_DEFAULT_ARGS);
          } else {
            this.plate.onDropFile(file, index);
          }
        }
      });
    }
  }
}
